import type { ComponentProps } from 'react';
import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import type { ApolloCache } from '@apollo/client';
import { cacheModify, useFragment } from '@frameio/graphql-client';
import type {
  BulkDeleteShareMutation,
  DeleteShareMutationResult,
  Project,
  Share,
  ShareNameFragment,
} from '@frameio/graphql-client/generated';
import {
  ShareName,
  useBulkDeleteShareMutation,
  useDeleteShareMutation,
} from '@frameio/graphql-client/generated';
import { plural } from '@frameio/i18n';
import { useActiveProjectId, useActiveShareId } from '@frameio/shared/contexts';
import { useSetSelectedShareContext } from '@frameio/shared/contexts/AllSharesListContext/SelectedShareContext';
import { DeleteConfirmation } from '@frameio/shared/resources';
import { getLink } from '@frameio/shared/routing/getLink';
import { useTrack } from '@frameio/shared/segment/hooks';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';
import { useToastTryCatchHandler } from '@frameio/utils/hooks/useToastTryCatchHandler';
import { Text, usePreviousDistinct } from '@frameio/vapor';
import TEXT from './generated/TEXT/useDeleteShare';

TEXT.DEFINE(
  {
    /** A message to display within the delete confirmation dialog when deleting one share */
    deleteConfirmationSingular: (name: string) => (
      <Text color="tertiary">
        Are you sure you want to delete "
        <Text color="primary" display="inline">
          {name}
        </Text>
        "? This action cannot be undone.
      </Text>
    ),
    /** A message to display within the delete confirmation dialog when deleting multiple shares */
    deleteConfirmationPlural: (count: number) =>
      `Are you sure you want to delete all ${count} shares? This action cannot be undone.`,
    /** A label to display for the delete confirmation button */
    deleteConfirmationButton: 'Delete',
    /** A title to display for the delete confirmation dialog */
    deleteTitle: ({ count }: { count: number }) =>
      plural(count, {
        one: `Delete Share`,
        other: `Delete ${count} Shares`,
      }),
    /** A message to display within a toast when the share is deleted */
    successToast: ({ count, name }: { count: number; name: string }) =>
      plural(count, {
        one: `${name} has been deleted`,
        other: `${count} shares have been deleted`,
      }),
    /** A message to display within a toast when an error occurs during the attempted deletion of a share */
    errorToast: 'Something went wrong when deleting Share',
  },
  { Text }
);

function updateProjectForShareDeletion(
  cache: ApolloCache<any>,
  projectId: Project['id'],
  results: Array<{ result?: Pick<Share, 'id' | 'name'> }>
) {
  results.forEach(({ result }) => {
    cache.evict({
      id: cache.identify({ __typename: 'Share', id: result?.id }),
    });
  });

  cacheModify<Project>(cache, {
    id: cache.identify({
      __typename: 'Project',
      id: projectId,
    }),
    fields: {
      shares: (value) => ({
        ...value,
        totalCount: value.totalCount - results.length,
      }),
    },
  });
}

type DeleteShareDialogProps = {
  shareName: string;
  count: number;
} & Pick<
  ComponentProps<typeof DeleteConfirmation>,
  'isShowing' | 'onRequestClose' | 'onConfirm'
>;

function DeleteShareDialog({
  shareName,
  onConfirm,
  isShowing,
  onRequestClose,
  count,
}: DeleteShareDialogProps) {
  return (
    <DeleteConfirmation
      confirmButtonText={TEXT.deleteConfirmationButton}
      isShowing={isShowing}
      message={
        count === 1
          ? TEXT.deleteConfirmationSingular(shareName)
          : TEXT.deleteConfirmationPlural(count)
      }
      // message={TEXT.deleteConfirmation({
      //   name: shareName,
      //   count,
      // })}
      onConfirm={onConfirm}
      onRequestClose={onRequestClose}
      requireExtraConfirmation={false}
      title={TEXT.deleteTitle({ count })?.toString() as string}
    />
  );
}

export function useDeleteShare() {
  const router = useRouter();

  const projectId = useActiveProjectId();
  const activeShareId = useActiveShareId();
  const [shareToDelete, setShareToDelete] = useState<ShareNameFragment>(null);
  const setActiveShare = useSetSelectedShareContext();
  const tryCatchOnDelete = useToastTryCatchHandler({ duration: 2000 });

  const [deleteShareMutation] = useDeleteShareMutation();

  const track = useTrack();

  const deleteShare = useCallback(() => {
    tryCatchOnDelete<DeleteShareMutationResult>({
      attempt: () =>
        deleteShareMutation({
          variables: {
            input: {
              shareId: shareToDelete.id,
            },
          },
          update: (cache, { data }) => {
            if (data?.deleteShare?.share) {
              updateProjectForShareDeletion(cache, projectId, [
                { result: data.deleteShare.share },
              ]);
            }
          },
        }),
      successMessage({ data }: DeleteShareMutationResult) {
        if (data?.deleteShare?.share?.id === activeShareId) {
          const href = getLink({ page: 'folder', projectId });
          router.push(href);
        }

        return TEXT.successToast({
          name: data?.deleteShare?.share?.name ?? '',
          count: 1,
        })?.toString() as string;
      },
      errorMessage: TEXT.errorToast,
    });
  }, [
    tryCatchOnDelete,
    deleteShareMutation,
    shareToDelete?.id,
    projectId,
    activeShareId,
    router,
  ]);

  const handleRequestClose = () => {
    setShareToDelete(null);
    setActiveShare(null);
  };

  const handleDelete = () => {
    deleteShare();
    handleRequestClose();
    track('share-deleted', {
      share_id: shareToDelete.id,
      num_shares_deleted: 1,
    });
  };

  const prevShare = usePreviousDistinct(shareToDelete);

  const share = shareToDelete ?? prevShare;

  const deleteDialog = (
    <DeleteShareDialog
      count={1}
      isShowing={Boolean(shareToDelete)}
      onConfirm={handleDelete}
      onRequestClose={handleRequestClose}
      shareName={share?.name}
    />
  );

  return {
    deleteDialog,
    requestDelete: setShareToDelete,
  };
}

type BulkDeleteSharesArgs = {
  projectId: Project['id'];
  shareIds: Array<Share['id']>;
};

export function useBulkDeleteShares({
  projectId,
  shareIds,
}: BulkDeleteSharesArgs) {
  const [isDeleteDialogShowing, setIsDeleteDialogShowing] = useState(false);
  const tryCatch = useToastTryCatchHandler();
  const shareCountToBeDeleted = shareIds?.length;

  const setActiveShare = useSetSelectedShareContext();

  const {
    data: { name: singleShareName },
  } = useFragment<ShareNameFragment>({
    from: {
      __typename: 'Share',
      id: shareIds?.length === 1 ? shareIds[0] : null,
    },
    fragment: ShareName,
  });

  const [bulkDeleteShares] = useBulkDeleteShareMutation({
    update: (cache, { data }) => {
      updateProjectForShareDeletion(
        cache,
        projectId,
        data?.bulkDeleteShares?.results
      );
    },
    /**
     * @note
     * We only want to refetch the query below in case when more than 50 shares
     * are attempted to be deleted. This is needed to refetch the data for Shares list
     * in the Projects Nav.
     */
    refetchQueries:
      shareCountToBeDeleted > DEFAULT_INITIAL_PAGE_SIZE
        ? [
            'GetSharesForProjectNavigation',
            'GetSharesForAllSharesTable',
            'GetSharesForAddToShareMenu',
          ]
        : undefined,
  });

  const track = useTrack();

  const tryDelete = () =>
    tryCatch<BulkDeleteShareMutation>({
      attempt: () =>
        bulkDeleteShares({
          variables: {
            input: { shareIds },
          },
        }),
      successMessage: TEXT.successToast({
        count: shareIds.length,
        name: singleShareName ?? '',
      })?.toString() as string,
      errorMessage: TEXT.errorToast,
    });

  const deleteDialog = (
    <DeleteShareDialog
      count={shareIds?.length}
      isShowing={isDeleteDialogShowing}
      onConfirm={() => {
        tryDelete();
        track('share-deleted', {
          share_id: shareIds.toString(),
          num_shares_deleted: shareIds.length,
        });
      }}
      onRequestClose={() => {
        setIsDeleteDialogShowing(false);
      }}
      shareName={singleShareName ?? ''}
    />
  );

  return {
    deleteDialog,
    requestDelete: () => {
      setIsDeleteDialogShowing(true);
      setActiveShare(null);
    },
  };
}
